import React, { useEffect, useRef } from "react";

export default function GoldBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Small twinkling stars
    const stars = Array.from({ length: 160 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.6 + 0.3,
      alpha: Math.random() * 0.6 + 0.2,
      speed: Math.random() * 0.02 + 0.004,
      offset: Math.random() * Math.PI * 2,
    }));

    // Bright halo stars
    const brightStars = Array.from({ length: 22 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 3 + 1.5,
      alpha: Math.random() * 0.5 + 0.4,
      speed: Math.random() * 0.015 + 0.005,
      offset: Math.random() * Math.PI * 2,
    }));

    // Shooting stars
    const makeShooter = () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight * 0.5,
      vx: 6 + Math.random() * 6,
      vy: 2 + Math.random() * 3,
      len: 80 + Math.random() * 100,
      alpha: 0,
      life: 0,
      maxLife: 60 + Math.random() * 40,
      active: false,
      timer: Math.random() * 180,
    });
    const shooters = Array.from({ length: 5 }, makeShooter);

    // Nebula clouds
    const nebulae = Array.from({ length: 4 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      rx: 150 + Math.random() * 180,
      ry: 80 + Math.random() * 120,
      alpha: 0.04 + Math.random() * 0.05,
      hue: Math.random() > 0.5 ? "37,99,235" : "59,130,246",
    }));

    let frame = 0;
    let animId;

    const draw = () => {
      frame++;

      // Dark navy background
      // ctx.fillStyle = "#050d1f";
      // ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Nebula clouds
      nebulae.forEach((n) => {
        ctx.save();
        ctx.translate(n.x, n.y);
        ctx.scale(1, n.ry / n.rx);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, n.rx);
        g.addColorStop(0, `rgba(${n.hue}, ${n.alpha})`);
        g.addColorStop(1, `rgba(${n.hue}, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, n.rx, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // ── Concentric dotted rings — TRUE CENTER ──────────────────────
      const cx = canvas.width * 0.5;
      const cy = canvas.height * 0.5;
      const maxR = Math.max(canvas.width, canvas.height) * 0.88;
      for (let r = 60; r < maxR; r += 48) {
        const numDots = Math.floor((2 * Math.PI * r) / 9);
        // Each ring pulses at different phase
        const pulse = 0.08 + 0.07 * Math.sin(frame * 0.006 + r * 0.012);
        for (let i = 0; i < numDots; i++) {
          const angle = (i / numDots) * Math.PI * 2;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          if (x < 0 || x > canvas.width || y < 0 || y > canvas.height) continue;
          ctx.beginPath();
          ctx.arc(x, y, 1, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(96, 165, 250, ${pulse})`;
          ctx.fill();
        }
      }

      // ── Central radial glow pulse ───────────────────────────────────
      const pulseScale = 0.5 + 0.5 * Math.sin(frame * 0.03);
      const centerGlow = ctx.createRadialGradient(
        cx,
        cy,
        0,
        cx,
        cy,
        200 + pulseScale * 60,
      );
      centerGlow.addColorStop(
        0,
        `rgba(37, 99, 235, ${0.18 + pulseScale * 0.08})`,
      );
      centerGlow.addColorStop(
        0.4,
        `rgba(29, 78, 216, ${0.07 + pulseScale * 0.04})`,
      );
      centerGlow.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = centerGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, 260 + pulseScale * 60, 0, Math.PI * 2);
      ctx.fill();

      // ── Small stars ─────────────────────────────────────────────────
      stars.forEach((s) => {
        const a = s.alpha * (0.4 + 0.6 * Math.sin(frame * s.speed + s.offset));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(147, 197, 253, ${a})`;
        ctx.fill();
      });

      // ── Bright halo stars ───────────────────────────────────────────
      brightStars.forEach((s) => {
        const a = s.alpha * (0.3 + 0.7 * Math.sin(frame * s.speed + s.offset));
        // Outer halo
        const halo = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 8);
        halo.addColorStop(0, `rgba(96, 165, 250, ${a * 0.7})`);
        halo.addColorStop(1, "rgba(96, 165, 250, 0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * 8, 0, Math.PI * 2);
        ctx.fill();
        // Cross sparkle
        ctx.strokeStyle = `rgba(186, 230, 253, ${a * 0.6})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(s.x - s.r * 10, s.y);
        ctx.lineTo(s.x + s.r * 10, s.y);
        ctx.moveTo(s.x, s.y - s.r * 10);
        ctx.lineTo(s.x, s.y + s.r * 10);
        ctx.stroke();
        // Core
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(219, 234, 254, ${a})`;
        ctx.fill();
      });

      // ── Shooting stars ──────────────────────────────────────────────
      shooters.forEach((sh) => {
        if (!sh.active) {
          sh.timer--;
          if (sh.timer <= 0) {
            sh.x = Math.random() * canvas.width * 0.6;
            sh.y = Math.random() * canvas.height * 0.4;
            sh.vx = 7 + Math.random() * 7;
            sh.vy = 2 + Math.random() * 4;
            sh.len = 90 + Math.random() * 110;
            sh.life = 0;
            sh.maxLife = 55 + Math.random() * 35;
            sh.active = true;
          }
          return;
        }
        sh.life++;
        const t = sh.life / sh.maxLife;
        const a = t < 0.3 ? t / 0.3 : 1 - (t - 0.3) / 0.7;
        const tailX = sh.x - (sh.vx / Math.hypot(sh.vx, sh.vy)) * sh.len * t;
        const tailY = sh.y - (sh.vy / Math.hypot(sh.vx, sh.vy)) * sh.len * t;
        const grad = ctx.createLinearGradient(tailX, tailY, sh.x, sh.y);
        grad.addColorStop(0, "rgba(147,197,253,0)");
        grad.addColorStop(1, `rgba(219,234,254,${a * 0.9})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(sh.x, sh.y);
        ctx.stroke();
        sh.x += sh.vx;
        sh.y += sh.vy;
        if (sh.life >= sh.maxLife) {
          sh.active = false;
          sh.timer = 80 + Math.random() * 160;
        }
      });

      // ── Corner glows ────────────────────────────────────────────────
      [
        [0, canvas.height],
        [canvas.width, canvas.height],
        [0, 0],
        [canvas.width, 0],
      ].forEach(([gx, gy], i) => {
        const g = ctx.createRadialGradient(gx, gy, 0, gx, gy, 260);
        const intensity = i < 2 ? 0.35 : 0.18;
        g.addColorStop(0, `rgba(37, 99, 235, ${intensity})`);
        g.addColorStop(0.5, `rgba(29, 78, 216, ${intensity * 0.3})`);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.fillRect(gx - 260, gy - 260, 520, 520);
      });

      // Vignette
      const vig = ctx.createRadialGradient(
        cx,
        cy,
        canvas.height * 0.3,
        cx,
        cy,
        Math.max(canvas.width, canvas.height) * 0.75,
      );
      vig.addColorStop(0, "rgba(0,0,0,0)");
      vig.addColorStop(1, "rgba(0,0,10,0.6)");
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
      }}
    />
  );
}
